import mapboxgl from 'mapbox-gl'
import { createRoot } from 'react-dom/client'
import React from 'react'
import { UserPOI } from '../types/userPois'
import { UserPOIMarker } from '../components/userPois/UserPOIMarker'

export class UserPOIRenderer {
  private map: mapboxgl.Map
  private markers: Map<string, mapboxgl.Marker> = new Map()
  private selectedPOIId: string | null = null
  private onPOIClick: (poi: UserPOI) => void = () => {}
  private onPOIUpdate?: (poiId: string, coordinates: { lng: number; lat: number }) => void
  private pois: UserPOI[] = []

  constructor(map: mapboxgl.Map) {
    this.map = map
  }

  setOnPOIUpdate(callback: (poiId: string, coordinates: { lng: number; lat: number }) => void) {
    this.onPOIUpdate = callback
  }

  setOnPOIClick(callback: (poi: UserPOI) => void) {
    this.onPOIClick = callback
  }

  /**
   * Render user POI markers on the map
   */
  renderUserPOIs(pois: UserPOI[], selectedPOIId?: string, currentUserId?: string) {
    console.log('📍 UserPOIRenderer.renderUserPOIs called with:', pois.length, 'POIs')
    this.selectedPOIId = selectedPOIId || null
    this.pois = pois
    
    // Clear existing markers
    this.clearMarkers()

    // Create markers for each POI
    pois.forEach(poi => {
      this.createUserPOIMarker(poi, currentUserId)
    })
  }

  /**
   * Create a marker for a single user POI
   */
  private createUserPOIMarker(poi: UserPOI, currentUserId?: string) {
    // Create marker element
    const markerElement = document.createElement('div')
    markerElement.className = 'user-poi-marker'

    // Create React root and render UserPOIMarker component
    const root = createRoot(markerElement)
    root.render(
      React.createElement(UserPOIMarker, {
        poi,
        onClick: this.onPOIClick,
        isSelected: poi.id === this.selectedPOIId
      })
    )

    // Add click handler to the marker element
    markerElement.addEventListener('click', (e) => {
      e.stopPropagation()
      console.log('🔍 User POI clicked:', poi.name)
      this.onPOIClick(poi)
    })

    // Check if user can drag this POI (they own it)
    const isDraggable = currentUserId && poi.authorId === currentUserId && this.onPOIUpdate

    // Create Mapbox marker
    // Anchor at 'bottom' so the arrow tip points to the exact location
    const marker = new mapboxgl.Marker({
      element: markerElement,
      anchor: 'bottom',
      draggable: isDraggable
    })
      .setLngLat([poi.coordinates.lng, poi.coordinates.lat])
      .addTo(this.map)

    // Handle drag events if draggable
    if (isDraggable) {
      let hasBeenDragged = false
      
      marker.on('dragstart', () => {
        hasBeenDragged = true
      })
      
      marker.on('dragend', () => {
        if (hasBeenDragged) {
          const lngLat = marker.getLngLat()
          this.onPOIUpdate!(poi.id, { lng: lngLat.lng, lat: lngLat.lat })
          hasBeenDragged = false
        }
      })
    }

    // Store marker for cleanup
    this.markers.set(poi.id, marker)
  }

  /**
   * Clear all user POI markers
   */
  clearMarkers() {
    this.markers.forEach(marker => {
      marker.remove()
    })
    this.markers.clear()
  }

  /**
   * Fly to a specific user POI
   */
  flyToPOI(poi: UserPOI) {
    this.map.flyTo({
      center: [poi.coordinates.lng, poi.coordinates.lat],
      duration: 1000
      // No zoom specified - just pan to location
    })
  }

  /**
   * Highlight a specific POI
   */
  highlightPOI(poiId: string) {
    this.selectedPOIId = poiId
    // Re-render to update selection state
    // In a production app, you'd update just the affected markers
  }

  /**
   * Enable moving mode for a specific POI
   */
  startMovePOI(poiId: string) {
    const marker = this.markers.get(poiId)
    const poi = this.pois.find(p => p.id === poiId)
    
    if (marker && poi) {
      // Make the marker draggable
      marker.setDraggable(true)
      
      // Add visual feedback
      const element = marker.getElement()
      element.style.cursor = 'move'
      element.classList.add('moving')
      
      // Fly to the POI
      this.flyToPOI(poi)
      
      // Show a temporary tooltip
      const popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: [0, -30]
      })
        .setLngLat([poi.coordinates.lng, poi.coordinates.lat])
        .setHTML('<div class="text-xs bg-gray-900 text-white px-2 py-1 rounded">Drag to move</div>')
        .addTo(this.map)
      
      // Remove tooltip after 3 seconds
      setTimeout(() => popup.remove(), 3000)
    }
  }
}